import React, { useEffect, useState } from 'react'
import AdminHeader from '../components/AdminHeader'
import { Alert } from '@mui/material';
import { AnnouncementService } from '../services/AnnouncementService';
import EntriesComponent from '../components/EntriesComponent';
import AnnouncementsTable from '../components/AnnouncementsTable';
import { NavLink } from 'react-router-dom';

const Announcements = () => {
    const [_error, _setError] = useState();
    const [data, setData] = useState();
    const [message, setMessage] = useState();
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        targetAudience: '',
        priority: '',
        isActive: ''
    });

    const deleteAnnouncement = async (announcementId) => {
        try {
            await AnnouncementService.deleteAnnouncement(announcementId);
            setMessage('Announcement has been deleted');
            fetchAnnouncements();
        } catch (error) {
            _setError('Failed to delete announcement');
        }
    };

    const toggleAnnouncementStatus = async (announcementId) => {
        try {
            await AnnouncementService.toggleAnnouncementStatus(announcementId);
            setMessage('Announcement status has been updated');
            fetchAnnouncements();
        } catch (error) {
            _setError('Failed to update announcement status');
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, [page, limit, filters, search]);

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit,
                ...filters
            };
            // Add search parameter if search term exists
            if (search && search.trim()) {
                params.search = search.trim();
            }
            const response = await AnnouncementService.getAllAnnouncements(params);
            setData(response.announcements);
            setTotalPages(response.pagination.totalPages);
        } catch (error) {
            setData([]);
            _setError('Failed to fetch announcements');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterKey, value) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
        setPage(1); // Reset to first page when filters change
    };

    return (
        <div className="flex w-full h-screen overflow-auto">
            <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
                <div className="px-10 bg-white">
                    <AdminHeader
                        heading={"Announcements"}
                        pages={"Pages / Announcements"}
                    />
                </div>
                {message &&
                    <Alert
                        severity="success"
                        sx={{
                            position: "fixed",
                            width: "90%",
                            margin: "auto",
                            top: 20,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                        }}
                    >
                        {message}
                    </Alert>
                }
                {_error &&
                    <Alert
                        severity="error"
                        sx={{
                            position: "fixed",
                            width: "90%",
                            margin: "auto",
                            top: 20,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                        }}
                    >
                        {_error}
                    </Alert>
                }
                <div className="px-6 py-5 h-full overflow-auto">
                    <div className="m-auto shadow-4xl pb-[30px] bg-white my-7 rounded-xl w-full tablet:w-[90%] gap-x-5 min-h-[80%]">
                        <div className="w-[95%] m-auto flex flex-col">
                            <div className="flex justify-between items-center mt-3 mb-3">
                                <NavLink to={`/announcements/stats`} className="p-1 px-4 rounded-[21px] flex gap-2 bg-green-500 text-white hover:bg-green-600">
                                    View Statistics
                                </NavLink>
                                <div className="flex gap-4">
                                    <select
                                        value={filters.targetAudience}
                                        onChange={(e) => handleFilterChange('targetAudience', e.target.value)}
                                        className="px-3 py-2 border rounded-md"
                                    >
                                        <option value="">All Audiences</option>
                                        <option value="all">All Users</option>
                                        <option value="guests">Guests</option>
                                        <option value="hosts">Hosts</option>
                                        <option value="drivers">Drivers</option>
                                        <option value="admins">Admins</option>
                                    </select>
                                    <select
                                        value={filters.priority}
                                        onChange={(e) => handleFilterChange('priority', e.target.value)}
                                        className="px-3 py-2 border rounded-md"
                                    >
                                        <option value="">All Priorities</option>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                    <select
                                        value={filters.isActive}
                                        onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                        className="px-3 py-2 border rounded-md"
                                    >
                                        <option value="">All Status</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <NavLink to={`/announcements/create`} className="p-1 px-4 rounded-[21px] flex gap-2 bg-blue text-white">
                                    Create New Announcement
                                </NavLink>
                            </div>
                            <div className="relative overflow-x-auto">
                                <EntriesComponent search={search} setSearch={setSearch} setLimit={setLimit} />
                                <AnnouncementsTable
                                    data={data}
                                    limit={limit}
                                    loading={loading}
                                    deleteAnnouncement={deleteAnnouncement}
                                    toggleAnnouncementStatus={toggleAnnouncementStatus}
                                />
                            </div>
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center mt-4 gap-2">
                                    <button
                                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                        disabled={page === 1}
                                        className="px-3 py-1 border rounded disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={page === totalPages}
                                        className="px-3 py-1 border rounded disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    )
}

export default Announcements
